import { useCallback, useEffect, useRef, useState } from "react";

interface IUseScrollTopAvailabilityOptions {
  activationScrollY?: number;
  scrollContainerId?: string;
  getScrollContainer?: () => HTMLElement | null;
}

export const useScrollTopAvailability = (
  options: IUseScrollTopAvailabilityOptions = {}
) => {
  const {
    activationScrollY = 120,
    scrollContainerId,
    getScrollContainer,
  } = options;

  const [isScrollTopEnabled, setIsScrollTopEnabled] = useState(false);

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const resolveScrollContainer = useCallback(() => {
    const byGetter = getScrollContainer?.();
    if (byGetter) return byGetter;

    if (scrollContainerId) {
      return document.getElementById(scrollContainerId);
    }

    return null;
  }, [getScrollContainer, scrollContainerId]);

  const getMetrics = useCallback(() => {
    const container = scrollContainerRef.current;

    if (container) {
      return {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        container,
      };
    }

    const root = document.documentElement;
    return {
      scrollTop: window.scrollY,
      scrollHeight: root.scrollHeight,
      clientHeight: root.clientHeight,
      container: null as HTMLElement | null,
    };
  }, []);

  const recalc = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = getMetrics();

    const isPageScrollable = scrollHeight - clientHeight > 1;
    const isPastActivationPoint = scrollTop > activationScrollY;

    const next = isPageScrollable && isPastActivationPoint;

    setIsScrollTopEnabled((prev) => (prev === next ? prev : next));
  }, [activationScrollY, getMetrics]);

  const scrollToTop = useCallback(() => {
    const { container } = getMetrics();

    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    const start = performance.now();
    let last = container ? container.scrollTop : window.scrollY;
    let lastChange = start;

    const tick = () => {
      const now = performance.now();
      const current = container ? container.scrollTop : window.scrollY;

      if (current <= 0) return;

      if (current !== last) {
        last = current;
        lastChange = now;
      }

      const stalled = now - lastChange > 120;
      const tooLong = now - start > 800;

      if (stalled || tooLong) {
        if (container) container.scrollTo({ top: 0 });
        else window.scrollTo({ top: 0 });
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, [getMetrics]);

  useEffect(() => {
    scrollContainerRef.current = resolveScrollContainer();

    recalc();

    const handle = () => recalc();
    const container = scrollContainerRef.current;

    if (container)
      container.addEventListener("scroll", handle, { passive: true });
    else window.addEventListener("scroll", handle, { passive: true });

    window.addEventListener("resize", handle);

    let resizeObserver: ResizeObserver | null = null;
    if (container) {
      resizeObserver = new ResizeObserver(handle);
      resizeObserver.observe(container);
    }

    return () => {
      if (container) container.removeEventListener("scroll", handle);
      else window.removeEventListener("scroll", handle);

      window.removeEventListener("resize", handle);
      resizeObserver?.disconnect();
    };
  }, [recalc, resolveScrollContainer]);

  return { isScrollTopEnabled, scrollToTop };
};
